import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useAgentStatus } from '../hooks/useAgentStatus';

// The agent's own presence control, in the sidebar. Shows the current status
// with a live timer and opens a menu of everything they can switch to:
// Available, each configured break type (with what's left of today's
// allowance), Not Ready reasons, Email, and Offline.
//
// The break types, their allowances and the Not Ready reasons all come from
// the server (GET /wfm/status/me) — the workspace owner configures them on
// WFM → Presence, and they're prorated to each agent's working hours.

export const STATUS_META = {
  available: { label: 'Available', dot: '#22c55e' },
  not_ready: { label: 'Not Ready', dot: '#f59e0b' },
  break: { label: 'Break', dot: '#38bdf8' },
  email: { label: 'Email', dot: '#a78bfa' },
  offline: { label: 'Offline', dot: '#94a3b8' },
};

// ms → "5:04" / "1:02:30" — a running clock, not a rounded duration.
export function fmtClock(ms) {
  if (ms == null || ms < 0) return '0:00';
  const total = Math.floor(ms / 1000);
  const s = total % 60;
  const m = Math.floor(total / 60) % 60;
  const h = Math.floor(total / 3600);
  const pad = n => String(n).padStart(2, '0');
  return h ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

// ms → "45m" / "1h 05m", for allowances rather than live timers.
export function fmtMinutes(ms) {
  const m = Math.max(0, Math.round(ms / 60000));
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}

export default function AgentStatusMenu() {
  const { status, reason, since, online, policy, usage, notReadyReasons, loading, changeStatus, refresh } = useAgentStatus();
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(Date.now());
  const wrapRef = useRef(null);

  // Live timer for the current status.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Reopening the menu is a good moment to re-read allowances (another tab may
  // have taken a break since the last poll).
  const toggle = () => {
    setOpen(o => {
      if (!o) refresh();
      return !o;
    });
  };

  const pick = async (next, nextReason, label) => {
    setOpen(false);
    try {
      await changeStatus(next, nextReason);
      toast.success(`Status: ${label}`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const meta = STATUS_META[status] || STATUS_META.offline;
  const breakTypes = usage?.breaks || [];
  const currentBreak = status === 'break' ? breakTypes.find(b => b.key === reason) : null;
  const elapsed = since ? now - new Date(since).getTime() : 0;
  // A break that has run past its own length — the agent sees it before a
  // supervisor does on the WFM Alerts tab.
  const overBy = currentBreak ? elapsed - currentBreak.minutes * 60000 : 0;

  const subLabel = currentBreak
    ? currentBreak.label
    : (status === 'not_ready' && reason
        ? (notReadyReasons.find(r => r.key === reason)?.label || reason)
        : null);

  const breakLeftMs = usage?.break_remaining_ms;
  const emailLeftMs = usage?.email_remaining_ms;

  return (
    <div className="status-widget" ref={wrapRef}>
      <button
        type="button"
        className={'status-trigger' + (open ? ' open' : '')}
        onClick={toggle}
        disabled={loading}
        title="Set your status"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Your status: ${meta.label}${subLabel ? `, ${subLabel}` : ''}. Change it`}
      >
        <span className="status-dot" style={{ background: meta.dot }} aria-hidden="true" />
        <span className="status-trigger-text">
          <span className="status-trigger-label">
            {meta.label}{subLabel ? ` · ${subLabel}` : ''}
          </span>
          <span className={'status-trigger-time' + (overBy > 0 ? ' over' : '')}>
            {status === 'offline' ? 'Logged off' : fmtClock(elapsed)}
            {overBy > 0 && ` · over by ${fmtMinutes(overBy)}`}
          </span>
        </span>
        <span className="status-caret">▾</span>
      </button>

      {open && (
        <div className="status-menu" role="menu">
          <div className="status-menu-head">
            {online ? 'Online' : 'Offline'}
            {online && since && ` · ${meta.label.toLowerCase()} for ${fmtClock(elapsed)}`}
          </div>

          <button type="button" className="status-option" role="menuitemradio"
            aria-checked={status === 'available'} onClick={() => pick('available', null, 'Available')}>
            <span className="status-dot" aria-hidden="true" style={{ background: STATUS_META.available.dot }} />
            <span className="status-option-label">Available <span className="muted">/ Ready</span></span>
            {status === 'available' && <span className="status-check" aria-hidden="true">✓</span>}
          </button>

          <div className="status-menu-section">
            Break
            {breakLeftMs != null && (
              <span className="status-menu-allowance">{fmtMinutes(breakLeftMs)} left today</span>
            )}
          </div>
          {breakTypes.map(b => {
            const exhausted = b.remaining_count <= 0 || (breakLeftMs != null && breakLeftMs <= 0);
            return (
              <button
                key={b.key}
                type="button"
                className="status-option" role="menuitemradio"
                aria-checked={status === 'break' && reason === b.key}
                disabled={exhausted}
                onClick={() => pick('break', b.key, b.label)}
                title={exhausted ? 'No allowance left today' : `${b.minutes} minutes`}
              >
                <span className="status-dot" aria-hidden="true" style={{ background: STATUS_META.break.dot }} />
                <span className="status-option-label">{b.label}</span>
                <span className="status-option-meta">
                  {b.minutes}m{b.max_per_day > 1 ? ` · ${b.remaining_count}/${b.max_per_day} left` : (exhausted ? ' · used' : '')}
                </span>
                {status === 'break' && reason === b.key && <span className="status-check" aria-hidden="true">✓</span>}
              </button>
            );
          })}

          <div className="status-menu-section">Not Ready</div>
          {notReadyReasons.map(r => (
            <button
              key={r.key}
              type="button"
              className="status-option" role="menuitemradio"
              aria-checked={status === 'not_ready' && reason === r.key}
              onClick={() => pick('not_ready', r.key, r.label)}
            >
              <span className="status-dot" aria-hidden="true" style={{ background: STATUS_META.not_ready.dot }} />
              <span className="status-option-label">{r.label}</span>
              {status === 'not_ready' && reason === r.key && <span className="status-check" aria-hidden="true">✓</span>}
            </button>
          ))}

          {policy?.email_enabled && (
            <>
              <div className="status-menu-section">Email</div>
              <button
                type="button"
                className="status-option" role="menuitemradio"
                aria-checked={status === 'email'}
                disabled={emailLeftMs != null && emailLeftMs <= 0}
                onClick={() => pick('email', null, 'Email')}
              >
                <span className="status-dot" aria-hidden="true" style={{ background: STATUS_META.email.dot }} />
                <span className="status-option-label">Working email</span>
                <span className="status-option-meta">
                  {emailLeftMs == null ? 'no cap' : `${fmtMinutes(emailLeftMs)} left`}
                </span>
                {status === 'email' && <span className="status-check" aria-hidden="true">✓</span>}
              </button>
            </>
          )}

          <div className="status-menu-divider" />
          <button type="button" className="status-option" role="menuitemradio"
            aria-checked={status === 'offline'} onClick={() => pick('offline', null, 'Offline')}>
            <span className="status-dot" aria-hidden="true" style={{ background: STATUS_META.offline.dot }} />
            <span className="status-option-label">Offline <span className="muted">— log off</span></span>
            {status === 'offline' && <span className="status-check" aria-hidden="true">✓</span>}
          </button>
        </div>
      )}
    </div>
  );
}
